import ExcelJS from 'exceljs';

/**
 * XLSX-compatible shim using ExcelJS.
 * Provides the same API as `import * as XLSX from 'xlsx'` so callers only
 * need to change the import path.
 */

// ── Internal helpers ──────────────────────────────────────────────

function triggerDownload(buffer, fileName) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Public API (mirrors the old XLSX namespace) ───────────────────

/**
 * utils.json_to_sheet(data)
 *   Returns a lightweight sheet object holding the data + optional col widths.
 */
function json_to_sheet(data) {
  return { _data: data, '!cols': null };
}

/**
 * utils.book_new()
 */
function book_new() {
  return { _sheets: {}, SheetNames: [] };
}

/**
 * utils.book_append_sheet(wb, ws, name)
 */
function book_append_sheet(wb, ws, name) {
  wb._sheets[name] = ws;
  wb.SheetNames.push(name);
}

/**
 * utils.sheet_to_json(sheetProxy)
 *   For workbooks returned by `read()`: parses the sheet into JSON objects.
 *   This is synchronous because the heavy parsing already happened in read().
 */
function sheet_to_json(sheetProxy) {
  if (sheetProxy && sheetProxy._json != null) return sheetProxy._json;
  return [];
}

/**
 * writeFile(wb, fileName)
 *   Builds a real ExcelJS workbook from the lightweight proxy, then triggers
 *   a browser download. Returns a Promise (callers need not await it).
 */
async function writeFileAsync(wb, fileName) {
  const workbook = new ExcelJS.Workbook();
  for (const name of wb.SheetNames) {
    const sheetProxy = wb._sheets[name];
    const ws = workbook.addWorksheet(name);
    const data = sheetProxy._data || [];
    if (data.length === 0) continue;

    const headers = Object.keys(data[0]);
    // Apply column widths if set via worksheet["!cols"]
    const cols = sheetProxy['!cols'];
    ws.columns = headers.map((key, i) => ({
      header: key,
      key,
      width: cols && cols[i] ? cols[i].wch || cols[i] : 20,
    }));
    data.forEach((row) => ws.addRow(row));
  }
  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownload(buffer, fileName);
}

// Fire-and-forget wrapper so callers don't need `await`
function writeFileFn(wb, fileName) {
  writeFileAsync(wb, fileName).catch((err) => console.error('Excel writeFile error:', err));
}

/**
 * read(binaryString, { type: 'binary' })
 *   Parses a binary string into a workbook proxy **synchronously** by doing the
 *   heavy conversion in-place (ExcelJS is async, so we cache via readBinaryAsync).
 *
 *   Because the original xlsx `read` was synchronous but ExcelJS is async,
 *   use the async helper `readBinaryAsJson` for new code.
 *   For backward compat with existing `XLSX.read` + `sheet_to_json` patterns,
 *   call `readAsync` and then access `.Sheets` / `.SheetNames`.
 */

/**
 * readAsync(binaryString)
 *   Async version: returns a workbook proxy whose sheets contain pre-parsed JSON.
 */
async function readAsync(binaryString) {
  const workbook = new ExcelJS.Workbook();
  const buf = new ArrayBuffer(binaryString.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < binaryString.length; i++) {
    view[i] = binaryString.charCodeAt(i) & 0xff;
  }
  await workbook.xlsx.load(buf);

  const proxy = { SheetNames: [], Sheets: {} };
  for (const sheet of workbook.worksheets) {
    proxy.SheetNames.push(sheet.name);

    const headers = [];
    sheet.getRow(1).eachCell((cell, col) => {
      headers[col] = cell.value != null ? String(cell.value).trim() : `Column${col}`;
    });
    const rows = [];
    sheet.eachRow((row, rowNum) => {
      if (rowNum === 1) return;
      const obj = {};
      row.eachCell((cell, col) => {
        if (headers[col]) obj[headers[col]] = cell.value;
      });
      if (Object.keys(obj).length > 0) rows.push(obj);
    });
    proxy.Sheets[sheet.name] = { _json: rows };
  }
  return proxy;
}

/**
 * Convenience: read binary → first sheet → JSON array (one async call).
 */
export async function readBinaryAsJson(binaryString) {
  const wb = await readAsync(binaryString);
  const firstSheet = wb.Sheets[wb.SheetNames[0]];
  return firstSheet ? firstSheet._json : [];
}

// Also export individual builders for direct use
export { jsonToWorkbook, writeFileAsync as writeFile };

/**
 * jsonToWorkbook — higher-level helper (used in already-migrated files).
 */
function jsonToWorkbook(data, sheetName = 'Sheet1', colWidths) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);
  if (!data || data.length === 0) return workbook;
  const headers = Object.keys(data[0]);
  worksheet.columns = headers.map((key, i) => ({
    header: key,
    key,
    width: colWidths && colWidths[i] ? colWidths[i] : 20,
  }));
  data.forEach((row) => worksheet.addRow(row));
  return workbook;
}

// ── Default export: drop-in namespace matching `import * as XLSX` ──

const XLSX = {
  utils: {
    json_to_sheet,
    book_new,
    book_append_sheet,
    sheet_to_json,
  },
  writeFile: writeFileFn,
  read: null, // Cannot be sync — see readAsync
  readAsync,
};

export default XLSX;
